import { useRef, useEffect, useState, useCallback } from 'react';
import { ArrowRight, Play, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useMousePosition } from '../lib/useScrollReveal';
import ParticleNetwork from './ParticleNetwork';

const ROTATING_WORDS = ['digital', 'web', 'SaaS', 'AI-powered', 'scalable', 'enterprise', 'cloud'];

const HERO_SLIDES = [
  {
    src: 'https://images.pexels.com/photos/196644/pexels-photo-196644.jpeg?auto=compress&cs=tinysrgb&w=800&q=75',
    alt: 'UI UX designer working on wireframes and prototypes',
  },
  {
    src: 'https://images.pexels.com/photos/574071/pexels-photo-574071.jpeg?auto=compress&cs=tinysrgb&w=800&q=75',
    alt: 'Developer writing clean code on laptop screen',
  },
  {
    src: 'https://images.pexels.com/photos/669610/pexels-photo-669610.jpeg?auto=compress&cs=tinysrgb&w=800&q=75',
    alt: 'Analytics dashboard showing business growth metrics',
  },
  {
    src: 'https://images.pexels.com/photos/3861958/pexels-photo-3861958.jpeg?auto=compress&cs=tinysrgb&w=800&q=75',
    alt: 'Modern digital agency workspace with multiple screens',
  },
];

const INTERVAL_MS = 3800;

interface Particle {
  id: number;
  x: number;
  y: number;
  angle: number;
  speed: number;
  size: number;
}

function CTAPrimaryButton({ children, className, to }: { children: React.ReactNode; className?: string; to: string }) {
  const btnRef = useRef<HTMLAnchorElement>(null);
  const [ripples, setRipples] = useState<{ id: number; x: number; y: number }[]>([]);
  const [particles, setParticles] = useState<Particle[]>([]);
  const rippleId = useRef(0);
  const particleId = useRef(0);

  const onMouseMove = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (e.clientX - cx) * 0.28;
    const dy = (e.clientY - cy) * 0.28;
    btn.style.transform = `translate(${dx}px, ${dy}px)`;
  }, []);

  const onMouseLeave = useCallback(() => {
    if (btnRef.current) btnRef.current.style.transform = 'translate(0, 0)';
  }, []);

  const onClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const id = ++rippleId.current;
    setRipples((prev) => [...prev, { id, x, y }]);
    setTimeout(() => setRipples((prev) => prev.filter((r) => r.id !== id)), 700);

    const newParticles: Particle[] = Array.from({ length: 10 }, (_, i) => ({
      id: ++particleId.current,
      x,
      y,
      angle: (360 / 10) * i + Math.random() * 20,
      speed: 40 + Math.random() * 50,
      size: 3 + Math.random() * 4,
    }));
    setParticles((prev) => [...prev, ...newParticles]);
    setTimeout(() => {
      const ids = newParticles.map((p) => p.id);
      setParticles((prev) => prev.filter((p) => !ids.includes(p.id)));
    }, 600);
  }, []);

  return (
    <Link
      to={to}
      ref={btnRef as React.Ref<HTMLAnchorElement>}
      className={`btn-magnetic hero-cta-primary flex items-center justify-center gap-2 group select-none ${className ?? ''}`}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      {ripples.map((r) => (
        <span
          key={r.id}
          className="cta-ripple"
          style={{ left: r.x, top: r.y }}
        />
      ))}
      {particles.map((p) => (
        <span
          key={p.id}
          className="cta-particle"
          style={{
            left: p.x,
            top: p.y,
            width: p.size,
            height: p.size,
            '--angle': `${p.angle}deg`,
            '--speed': `${p.speed}px`,
          } as React.CSSProperties}
        />
      ))}
      {children}
    </Link>
  );
}

function MagneticButton({ children, className, to, href }: { children: React.ReactNode; className: string; to?: string; href?: string }) {
  const btnRef = useRef<HTMLAnchorElement>(null);

  const onMouseMove = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (e.clientX - cx) * 0.3;
    const dy = (e.clientY - cy) * 0.3;
    btn.style.transform = `translate(${dx}px, ${dy}px)`;
  }, []);

  const onMouseLeave = useCallback(() => {
    if (btnRef.current) btnRef.current.style.transform = 'translate(0, 0)';
  }, []);

  const sharedProps = {
    ref: btnRef,
    className: `btn-magnetic ${className}`,
    onMouseMove,
    onMouseLeave,
  };

  if (to) {
    return (
      <Link to={to} {...sharedProps as React.ComponentProps<typeof Link>}>
        {children}
      </Link>
    );
  }

  return (
    <a href={href || '#'} {...sharedProps}>
      {children}
    </a>
  );
}

function RotatingWord({ word, animating }: { word: string; animating: boolean }) {
  return (
    <span
      className="hero-rotating-word hero-gradient-text inline-block"
      style={{
        opacity: animating ? 0 : 1,
        transform: animating ? 'translateY(12px)' : 'translateY(0)',
        transition: animating
          ? 'opacity 0.25s ease, transform 0.25s ease'
          : 'opacity 0.35s cubic-bezier(0.16,1,0.3,1) 0.05s, transform 0.35s cubic-bezier(0.16,1,0.3,1) 0.05s',
      }}
    >
      {word}
    </span>
  );
}

function StaggeredHeading({ isVisible, currentWord, animating }: { isVisible: boolean; currentWord: string; animating: boolean }) {
  const [filled, setFilled] = useState(false);

  useEffect(() => {
    if (isVisible) {
      const t = setTimeout(() => setFilled(true), 900);
      return () => clearTimeout(t);
    }
  }, [isVisible]);

  return (
    <>
      <h1 className="sr-only">SaaS &amp; Web App Development Company in India</h1>
      <div aria-hidden="true" className="mb-8 leading-[0.92] tracking-tight">
      <div className="overflow-visible mb-1">
        <div
          className="text-5xl sm:text-6xl md:text-7xl font-extrabold text-gray-900 dark:text-white"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(48px)',
            transition: 'opacity 0.8s cubic-bezier(0.16,1,0.3,1) 0ms, transform 0.8s cubic-bezier(0.16,1,0.3,1) 0ms',
          }}
        >
          We craft
        </div>
      </div>

      <div className="overflow-visible mb-1">
        <div
          className="text-5xl sm:text-6xl md:text-7xl font-extrabold"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(48px)',
            transition: 'opacity 0.8s cubic-bezier(0.16,1,0.3,1) 120ms, transform 0.8s cubic-bezier(0.16,1,0.3,1) 120ms',
          }}
        >
          <RotatingWord word={currentWord} animating={animating} />
        </div>
      </div>

      <div className="overflow-hidden mb-1">
        <div
          className="text-5xl sm:text-6xl md:text-7xl font-extrabold text-gray-900 dark:text-white"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(48px)',
            transition: 'opacity 0.8s cubic-bezier(0.16,1,0.3,1) 240ms, transform 0.8s cubic-bezier(0.16,1,0.3,1) 240ms',
          }}
        >
          <span className="flex items-end gap-3">
            experiences
            <svg className="mb-2 md:mb-3 flex-shrink-0" width="36" height="36" viewBox="0 0 36 36" fill="none"
              style={{ opacity: isVisible ? 1 : 0, transition: 'opacity 0.5s ease 500ms' }}>
              <path
                d="M8 18 L14 24 L28 10"
                stroke="url(#chkGrad)"
                strokeWidth="3.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="1"
                strokeDashoffset={filled ? 0 : 1}
                pathLength="1"
                style={{ transition: 'stroke-dashoffset 0.5s cubic-bezier(0.4,0,0.2,1) 0.6s' }}
              />
              <defs>
                <linearGradient id="chkGrad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#10b981" />
                  <stop offset="100%" stopColor="#06b6d4" />
                </linearGradient>
              </defs>
            </svg>
            <span className="text-5xl sm:text-6xl md:text-7xl font-extrabold text-gray-900 dark:text-white mb-0">.</span>
          </span>
        </div>
      </div>
    </div>
    </>
  );
}

function HeroImageSlider({
  slides,
  activeIndex,
  mouse,
  parallaxOffset,
}: {
  slides: typeof HERO_SLIDES;
  activeIndex: number;
  mouse: { x: number; y: number };
  parallaxOffset: number;
}) {
  return (
    <div
      className="relative rounded-2xl overflow-hidden shadow-2xl shadow-emerald-500/10 dark:shadow-emerald-500/5"
      style={{
        transform: `perspective(1000px) rotateY(${mouse.x * -3}deg) rotateX(${mouse.y * 2}deg) translateY(${-parallaxOffset}px)`,
        transition: 'transform 0.1s ease-out',
      }}
    >
      <div className="relative w-full aspect-[4/5] bg-gray-100 dark:bg-gray-800">
        {slides.map((slide, i) => (
          <img
            key={slide.src}
            src={slide.src}
            alt={slide.alt}
            loading={i === 0 ? 'eager' : 'lazy'}
            decoding={i === 0 ? 'sync' : 'async'}
            fetchPriority={i === 0 ? 'high' : 'low'}
            width="800"
            height="1000"
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              opacity: i === activeIndex ? 1 : 0,
              transform: i === activeIndex ? 'scale(1.04)' : 'scale(1)',
              transition: 'opacity 0.9s cubic-bezier(0.4,0,0.2,1), transform 5s ease-out',
            }}
          />
        ))}
      </div>

      <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 via-transparent to-transparent pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-cyan-500/5 pointer-events-none" />
      <div className="absolute inset-0 hero-image-overlay pointer-events-none" />

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {slides.map((_, i) => (
          <div
            key={i}
            className="rounded-full transition-all duration-500"
            style={{
              width: i === activeIndex ? '20px' : '6px',
              height: '6px',
              background: i === activeIndex ? 'rgba(16,185,129,0.9)' : 'rgba(255,255,255,0.4)',
            }}
          />
        ))}
      </div>
    </div>
  );
}

function ScrollIndicator() {
  return (
    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 scroll-indicator-fade">
      <span className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-widest">Scroll</span>
      <div className="relative w-6 h-10 rounded-full border-2 border-gray-300 dark:border-gray-600 flex items-start justify-center pt-1.5">
        <div className="scroll-dot w-1.5 h-1.5 bg-emerald-500 rounded-full" />
      </div>
      <ChevronDown size={16} className="text-gray-400 dark:text-gray-500 scroll-chevron" />
    </div>
  );
}

function FloatingCard({ children, className, delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  return (
    <div
      className={`floating-card bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 ${className}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export default function Hero() {
  const [heroVisible, setHeroVisible] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [wordIndex, setWordIndex] = useState(0);
  const [imageIndex, setImageIndex] = useState(0);
  const [wordAnimating, setWordAnimating] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const mouse = useMousePosition();

  useEffect(() => {
    const heroNode = heroRef.current;
    if (!heroNode) return;
    const heroObs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setHeroVisible(true); heroObs.unobserve(heroNode); }
    }, { threshold: 0.05 });
    heroObs.observe(heroNode);
    return () => heroObs.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!heroVisible) return;

    const timer = setInterval(() => {
      setWordAnimating(true);

      setTimeout(() => {
        setWordIndex((prev) => (prev + 1) % ROTATING_WORDS.length);
        setImageIndex((prev) => (prev + 1) % HERO_SLIDES.length);
        setWordAnimating(false);
      }, 300);
    }, INTERVAL_MS);

    return () => clearInterval(timer);
  }, [heroVisible]);

  const parallaxOffset = scrollY * 0.18;

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0 bg-gray-50 dark:bg-gray-950" />

      <div className="hero-ambient-glow absolute top-0 right-0 w-[800px] h-[800px] pointer-events-none" />
      <div className="hero-ambient-glow-2 absolute bottom-0 left-0 w-[600px] h-[600px] pointer-events-none" />

      <div className="mesh-orb-1 absolute top-[-20%] right-[-10%] w-[700px] h-[700px] rounded-full"
        style={{ background: 'radial-gradient(ellipse at center, rgba(16,185,129,0.13) 0%, rgba(20,184,166,0.06) 50%, transparent 75%)' }} />
      <div className="mesh-orb-2 absolute bottom-[-25%] left-[-15%] w-[600px] h-[600px] rounded-full"
        style={{ background: 'radial-gradient(ellipse at center, rgba(6,182,212,0.11) 0%, rgba(16,185,129,0.05) 50%, transparent 75%)' }} />
      <div className="mesh-orb-3 absolute top-[40%] left-[30%] w-[400px] h-[400px] rounded-full"
        style={{ background: 'radial-gradient(ellipse at center, rgba(52,211,153,0.07) 0%, transparent 70%)' }} />

      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 transition-transform duration-75"
          style={{ transform: `translate(${mouse.x * -8}px, ${mouse.y * -8}px)` }}>
          <div className="mesh-orb-1 absolute top-[-20%] right-[-10%] w-[700px] h-[700px] rounded-full opacity-50"
            style={{ background: 'radial-gradient(ellipse at center, rgba(16,185,129,0.08) 0%, transparent 60%)' }} />
        </div>
      </div>

      <ParticleNetwork className="opacity-60 dark:opacity-40 pointer-events-none" />

      <div className="absolute inset-0 opacity-[0.025] dark:opacity-[0.04] pointer-events-none"
        style={{ backgroundImage: 'linear-gradient(rgba(16,185,129,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,0.5) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

      <div className="relative container-narrow pt-32 pb-20 z-10">
        <div ref={heroRef} className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <div
              style={{
                opacity: heroVisible ? 1 : 0,
                transform: heroVisible ? 'translateY(0)' : 'translateY(24px)',
                transition: 'opacity 0.6s ease, transform 0.6s ease',
              }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200/60 dark:border-emerald-500/25 mb-8">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">Available for new projects</span>
              </div>
            </div>

            <StaggeredHeading
              isVisible={heroVisible}
              currentWord={ROTATING_WORDS[wordIndex]}
              animating={wordAnimating}
            />

            <div
              style={{
                opacity: heroVisible ? 1 : 0,
                transform: heroVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 0.7s ease 600ms, transform 0.7s ease 600ms',
              }}
            >
              <h2 className="text-lg md:text-xl text-gray-600 dark:text-gray-400 max-w-lg leading-relaxed mb-10 font-normal">
                From business websites to enterprise ERP systems — we build scalable, secure, and innovative solutions that transform how companies operate.
              </h2>
            </div>

            <div
              style={{
                opacity: heroVisible ? 1 : 0,
                transform: heroVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 0.7s ease 900ms, transform 0.7s ease 900ms',
              }}
              className="flex flex-col sm:flex-row gap-4 mb-16"
            >
              <CTAPrimaryButton to="/contact">
                Start Your Project
                <ArrowRight size={18} className="hero-cta-arrow transition-transform duration-300" />
              </CTAPrimaryButton>

              <MagneticButton
                to="/portfolio"
                className="hero-cta-secondary flex items-center justify-center gap-3 group"
              >
                <div className="hero-play-btn w-10 h-10 bg-gray-900 dark:bg-white rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300">
                  <Play size={14} className="text-white dark:text-gray-900 ml-0.5 group-hover:scale-110 transition-transform duration-300" fill="currentColor" />
                </div>
                View Our Work
              </MagneticButton>
            </div>

            <div
              style={{
                opacity: heroVisible ? 1 : 0,
                transform: heroVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 0.7s ease 1100ms, transform 0.7s ease 1100ms',
              }}
              className="flex items-center gap-3"
            >
              <div className="flex -space-x-3">
                {[
                  { src: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=100', label: 'Happy client of Matchless Digital Hub' },
                  { src: 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=100', label: 'Satisfied business owner' },
                  { src: 'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=100', label: 'Digital agency client' },
                  { src: 'https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=100', label: 'Technology startup founder' },
                ].map(({ src, label }, i) => (
                  <img key={i} src={src} alt={label} className="w-10 h-10 rounded-full border-2 border-white dark:border-gray-900 object-cover" loading="lazy" decoding="async" width="40" height="40" />
                ))}
              </div>
              <div>
                <div className="flex items-center gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <svg key={i} className="w-4 h-4 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Trusted by 25+ businesses</p>
              </div>
            </div>
          </div>

          <div
            className={`relative hidden lg:block transition-all duration-1000 delay-300 ${heroVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-16'}`}
          >
            <div className="relative" ref={imageRef}>
              <HeroImageSlider
                slides={HERO_SLIDES}
                activeIndex={imageIndex}
                mouse={mouse}
                parallaxOffset={parallaxOffset}
              />

              <FloatingCard
                className="absolute -bottom-6 -left-6 p-5 floating-card-glow"
                delay={0}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-500/20 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Performance</div>
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">99.9%</div>
                <div className="text-xs text-emerald-600 dark:text-emerald-400 font-medium mt-1">Uptime guaranteed</div>
              </FloatingCard>

              <FloatingCard
                className="absolute -top-4 -right-4 px-4 py-3 floating-card-glow"
                delay={1500}
              >
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Project delivered</span>
                </div>
              </FloatingCard>

              <div
                className="absolute top-1/2 -right-6 floating-card bg-white dark:bg-gray-800 rounded-2xl px-4 py-3 shadow-xl border border-gray-100 dark:border-gray-700 floating-card-glow"
                style={{ animationDelay: '800ms' }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-6 h-6 bg-cyan-100 dark:bg-cyan-500/20 rounded-md flex items-center justify-center">
                    <svg className="w-3 h-3 text-cyan-600 dark:text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Sprint done</span>
                </div>
                <div className="text-[10px] text-gray-400 dark:text-gray-500">2 hours ago</div>
              </div>

              <div className="absolute top-1/2 -left-8 w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl rotate-12 opacity-20 animate-morph" />
            </div>
          </div>
        </div>
      </div>

      <ScrollIndicator />
    </section>
  );
}
